if (!customElements.get('kronk-kit-builder')) {
  customElements.define(
    'kronk-kit-builder',
    class KronkKitBuilder extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-kit-form]');
        this.submitButton = this.querySelector('[data-kit-submit]');
        this.submitLabel = this.querySelector('[data-kit-submit-label]');
        this.message = this.querySelector('[data-kit-message]');
        this.spinner = this.querySelector('.loading__spinner');
        this.componentCount = parseInt(this.dataset.kitComponentCount || '0', 10);

        if (!this.form || !this.submitButton) return;

        this.form.addEventListener('change', this.updateState.bind(this));
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.updateState();
      }

      getSelectedComponents() {
        const selected = [];

        for (let slot = 1; slot <= this.componentCount; slot += 1) {
          const control = this.querySelector(
            `[data-kit-component-input][data-kit-component-slot="${slot}"]:checked, input[type="hidden"][data-kit-component-input][data-kit-component-slot="${slot}"]`
          );

          if (!control || !control.value || control.disabled) continue;

          selected.push({
            id: control.value,
            slot,
            item: control.dataset.kitItem || `Kit item ${slot}`,
            productTitle: control.dataset.kitProductTitle || '',
            variantTitle: control.dataset.kitVariantTitle || '',
          });
        }

        return selected;
      }

      updateState() {
        const selected = this.getSelectedComponents();
        const complete = selected.length === this.componentCount;

        this.submitButton.disabled = !complete;
        this.submitButton.setAttribute('aria-disabled', complete ? 'false' : 'true');

        if (!complete) {
          this.setMessage('Choose all available kit options before adding to cart.', false);
        } else {
          this.setMessage('', false);
        }
      }

      setMessage(text, isError = false) {
        if (!this.message) return;
        this.message.textContent = text || '';
        this.message.classList.toggle('is-error', Boolean(isError));
      }

      setLoading(isLoading) {
        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('is-loading', isLoading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
        if (this.submitLabel) this.submitLabel.textContent = isLoading ? 'Adding kit...' : `Add kit to cart - ${this.dataset.kitPrice}`;
      }

      getCartRenderer() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      getSectionsToRender(cartRenderer) {
        const sections = new Set();

        if (cartRenderer && typeof cartRenderer.getSectionsToRender === 'function') {
          cartRenderer.getSectionsToRender().forEach((section) => {
            if (section && section.id) sections.add(section.id);
          });
        }

        if (!sections.size) {
          sections.add('cart-drawer');
          sections.add('cart-icon-bubble');
        }

        return Array.from(sections);
      }

      hasRenderableSections(sections, sectionIds) {
        return Boolean(
          sections &&
            sectionIds.length &&
            sectionIds.every((sectionId) => typeof sections[sectionId] === 'string' && sections[sectionId].length)
        );
      }

      async fetchCartSections(sectionIds) {
        const url = new URL(window.routes?.cart_url || '/cart', window.location.origin);
        url.searchParams.set('sections', sectionIds.join(','));

        const response = await fetch(url.toString(), {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) throw new Error('Unable to refresh the cart drawer.');

        return response.json();
      }

      getRequiredProductIds() {
        return (this.dataset.kitRequiredProducts || '')
          .split(',')
          .map((productId) => Number(productId.trim()))
          .filter(Boolean);
      }

      cartHasRequiredProducts(cart) {
        const requiredProductIds = this.getRequiredProductIds();
        if (!requiredProductIds.length) return true;

        if (window.KronkKitDiscounts?.cartHasRequiredProducts) {
          return window.KronkKitDiscounts.cartHasRequiredProducts(cart, requiredProductIds);
        }

        if (!Array.isArray(cart?.items)) return false;

        const quantitiesByProduct = cart.items.reduce((quantities, item) => {
          const productId = Number(item.product_id);
          const quantity = Number(item.quantity) || 0;
          if (productId && quantity > 0) quantities.set(productId, (quantities.get(productId) || 0) + quantity);
          return quantities;
        }, new Map());

        return requiredProductIds.every((productId) => (quantitiesByProduct.get(productId) || 0) > 0);
      }

      async fetchCart() {
        if (window.KronkKitDiscounts?.fetchCart) return window.KronkKitDiscounts.fetchCart();

        const response = await fetch(window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js', {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) throw new Error('Unable to validate kit contents.');

        return response.json();
      }

      async applyDiscountCode(sectionIds) {
        const discountCode = (this.dataset.kitDiscountCode || '').trim();
        if (!discountCode) return null;

        const cart = await this.fetchCart();
        if (!this.cartHasRequiredProducts(cart)) {
          if (window.KronkKitDiscounts?.clearInvalidDiscount) {
            try {
              await window.KronkKitDiscounts.clearInvalidDiscount(cart, sectionIds, window.location.pathname);
            } catch (error) {
              // The add still succeeds; this guard only prevents unintended auto-discounting.
            }
          }
          return null;
        }

        const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: discountCode,
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        });

        let data;
        try {
          data = await response.json();
        } catch (error) {
          throw new Error('Kit was added, but the kit savings could not be applied.');
        }

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Kit was added, but the kit savings could not be applied.');
        }

        const appliedDiscount = Array.isArray(data.discount_codes)
          ? data.discount_codes.find((discount) => discount.code?.toLowerCase() === discountCode.toLowerCase())
          : null;

        if (appliedDiscount && appliedDiscount.applicable === false) {
          throw new Error('Kit was added, but the kit savings did not apply. Please refresh the cart and try again.');
        }

        if (!data.total_discount || data.total_discount <= 0) {
          throw new Error('Kit was added, but the kit savings did not apply. Please refresh the cart and try again.');
        }

        return data;
      }

      async onSubmit(event) {
        event.preventDefault();

        const selected = this.getSelectedComponents();
        if (selected.length !== this.componentCount) {
          this.setMessage('Choose all available kit options before adding to cart.', true);
          return;
        }

        this.setLoading(true);
        this.setMessage('', false);

        try {
          const cartRenderer = this.getCartRenderer();
          const sectionIds = this.getSectionsToRender(cartRenderer);
          const kitTitle = this.dataset.kitTitle || 'KRONK Kit';
          const kitId = this.dataset.kitId || kitTitle.toLowerCase().replace(/\s+/g, '-');

          const payload = {
            items: selected.map((component) => ({
              id: Number(component.id),
              quantity: 1,
              properties: {
                Kit: kitTitle,
                'Kit item': component.item,
                _kit_id: kitId,
                _kit_price: this.dataset.kitPrice || '',
              },
            })),
            sections: sectionIds,
            sections_url: window.location.pathname,
          };

          if (cartRenderer && typeof cartRenderer.setActiveElement === 'function') {
            cartRenderer.setActiveElement(document.activeElement);
          }

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unable to add this kit to cart.');
          }

          const addSections = data.sections;
          const discountData = await this.applyDiscountCode(sectionIds);

          if (this.hasRenderableSections(discountData?.sections, sectionIds)) {
            data.sections = discountData.sections;
          } else if (discountData || !this.hasRenderableSections(data.sections, sectionIds)) {
            try {
              data.sections = await this.fetchCartSections(sectionIds);
            } catch (sectionError) {
              data.sections = addSections;
            }
          }

          if (
            cartRenderer &&
            typeof cartRenderer.renderContents === 'function' &&
            this.hasRenderableSections(data.sections, sectionIds)
          ) {
            cartRenderer.renderContents(data);
          } else if (cartRenderer && typeof cartRenderer.open === 'function') {
            cartRenderer.open();
            this.setMessage('Kit added to cart. Open the cart drawer to review your kit.', false);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          this.setMessage(error.message || 'Unable to add this kit to cart.', true);
        } finally {
          this.setLoading(false);
          this.updateState();
        }
      }
    }
  );
}
